/**
 * Pool Reports React Query Hooks
 *
 * Custom hooks for fetching pool reports data with caching, error handling,
 * and automatic refetching using TanStack Query
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { PoolReportsService } from '@/services/poolReportsService';
import type {
  ReportType,
  ReportsHeading,
  Report,
  Contribution,
  Vessel,
  VesselDetails,
  VesselPoolPoints,
  VesselMonthlyInfo,
  VesselVoyageInfo,
  VesselVoyageNote,
  VesselBunkerInfo,
  NetAllocation,
  Distribution,
  DistributionDate,
  Offhire,
  Downloadable,
} from '@/types/poolReports.types';

// Query key factories for better cache management
export const poolReportsKeys = {
  all: ['poolReports'] as const,
  lists: () => [...poolReportsKeys.all, 'list'] as const,
  list: (reportType: ReportType, poolId: number) =>
    [...poolReportsKeys.lists(), reportType, poolId] as const,
  summaries: () => [...poolReportsKeys.all, 'summary'] as const,
  summary: (fileId: number, reportType: ReportType) =>
    [...poolReportsKeys.summaries(), fileId, reportType] as const,
  contributions: () => [...poolReportsKeys.all, 'contribution'] as const,
  contribution: (fileId: number) => [...poolReportsKeys.contributions(), fileId] as const,
  ownerVessels: (fileId: number, ownerId: number) =>
    [...poolReportsKeys.contributions(), fileId, 'owner', ownerId] as const,
  vessel: (fileId: number, vesselId: number) =>
    [...poolReportsKeys.contributions(), fileId, 'vessel', vesselId] as const,
  vesselDetails: (fileId: number, vesselId: number, type: string) =>
    [...poolReportsKeys.vessel(fileId, vesselId), type] as const,
  netAllocations: () => [...poolReportsKeys.all, 'netAllocation'] as const,
  netAllocation: (fileId: number) => [...poolReportsKeys.netAllocations(), fileId] as const,
  netAllocationOwner: (fileId: number, ownerId: number) =>
    [...poolReportsKeys.netAllocations(), fileId, 'owner', ownerId] as const,
  distributions: () => [...poolReportsKeys.all, 'distribution'] as const,
  distribution: (fileId: number, date: string) =>
    [...poolReportsKeys.distributions(), fileId, date] as const,
  distributionDates: (fileId: number) =>
    [...poolReportsKeys.distributions(), fileId, 'dates'] as const,
  distributionOwner: (fileId: number, ownerId: number, date: string) =>
    [...poolReportsKeys.distributions(), fileId, ownerId, date] as const,
  offhires: () => [...poolReportsKeys.all, 'offhire'] as const,
  offhire: (fileId: number) => [...poolReportsKeys.offhires(), fileId] as const,
  offhireOwner: (fileId: number, ownerId: number) =>
    [...poolReportsKeys.offhires(), fileId, 'owner', ownerId] as const,
  offhireVessel: (fileId: number, vesselId: number) =>
    [...poolReportsKeys.offhires(), fileId, 'vessel', vesselId] as const,
  downloads: () => [...poolReportsKeys.all, 'download'] as const,
  download: (fileId: number) => [...poolReportsKeys.downloads(), fileId] as const,
};

// ========== REPORT LIST & SUMMARY HOOKS ==========

/**
 * Fetch report list for a specific pool and type
 */
export function useReportList(reportType: ReportType, poolId: number, enabled = true) {
  return useQuery({
    queryKey: poolReportsKeys.list(reportType, poolId),
    queryFn: () => PoolReportsService.getReportList(reportType, poolId),
    enabled: enabled && !!poolId,
    staleTime: 0,
  });
}

/**
 * Fetch summary headings and details
 */
export function useSummaryData(fileId: number, reportType: ReportType, enabled = true) {
  const headingsQuery = useQuery({
    queryKey: [...poolReportsKeys.summary(fileId, reportType), 'headings'],
    queryFn: () => PoolReportsService.getHeadings(fileId, reportType),
    enabled: enabled && !!fileId,
  });

  const detailsQuery = useQuery({
    queryKey: [...poolReportsKeys.summary(fileId, reportType), 'details'],
    queryFn: () => PoolReportsService.getDetails(fileId, reportType),
    enabled: enabled && !!fileId,
  });

  return {
    headings: headingsQuery.data,
    details: detailsQuery.data,
    isLoading: headingsQuery.isLoading || detailsQuery.isLoading,
    isError: headingsQuery.isError || detailsQuery.isError,
    error: headingsQuery.error || detailsQuery.error,
  };
}

/**
 * Fetch summary headings only
 */
export function useSummaryHeadings(
  fileId: number,
  reportType: ReportType,
  enabled = true
): UseQueryResult<ReportsHeading[]> {
  return useQuery({
    queryKey: [...poolReportsKeys.summary(fileId, reportType), 'headings'],
    queryFn: () => PoolReportsService.getHeadings(fileId, reportType),
    enabled: enabled && !!fileId,
  });
}

/**
 * Fetch summary details only
 */
export function useSummaryDetails(
  fileId: number,
  reportType: ReportType,
  enabled = true
): UseQueryResult<Report[]> {
  return useQuery({
    queryKey: [...poolReportsKeys.summary(fileId, reportType), 'details'],
    queryFn: () => PoolReportsService.getDetails(fileId, reportType),
    enabled: enabled && !!fileId,
  });
}

// ========== CONTRIBUTION TAB HOOKS ==========

/**
 * Fetch contributions list
 */
export function useContributions(fileId: number, enabled = true): UseQueryResult<Contribution[]> {
  return useQuery({
    queryKey: poolReportsKeys.contribution(fileId),
    queryFn: () => PoolReportsService.getContributions(fileId),
    enabled: enabled && !!fileId,
  });
}

/**
 * Fetch owner vessels (contribution drill-down)
 */
export function useOwnerVessels(
  fileId: number,
  ownerId: number,
  enabled = true
): UseQueryResult<Vessel[]> {
  return useQuery({
    queryKey: poolReportsKeys.ownerVessels(fileId, ownerId),
    queryFn: () => PoolReportsService.getOwnerVessels(fileId, ownerId),
    enabled: enabled && !!fileId && !!ownerId,
  });
}

/**
 * Fetch all vessel details (6 data sections)
 */
export function useVesselAllDetails(fileId: number, vesselId: number, enabled = true) {
  const detailsQuery = useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'details'),
    queryFn: () => PoolReportsService.getVesselDetails(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });

  const poolPointsQuery = useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'poolpoints'),
    queryFn: () => PoolReportsService.getVesselPoolPoints(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });

  const monthlyInfoQuery = useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'monthlyinfo'),
    queryFn: () => PoolReportsService.getVesselMonthlyInfo(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });

  const voyageInfoQuery = useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'voyageinfo'),
    queryFn: () => PoolReportsService.getVesselVoyageInfo(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });

  const voyageNotesQuery = useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'voyagenotes'),
    queryFn: () => PoolReportsService.getVesselVoyageNotes(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });

  const bunkerInfoQuery = useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'bunkerinfo'),
    queryFn: () => PoolReportsService.getVesselBunkerInfo(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });

  return {
    details: detailsQuery.data,
    poolPoints: poolPointsQuery.data,
    monthlyInfo: monthlyInfoQuery.data,
    voyageInfo: voyageInfoQuery.data,
    voyageNotes: voyageNotesQuery.data,
    bunkerInfo: bunkerInfoQuery.data,
    isLoading:
      detailsQuery.isLoading ||
      poolPointsQuery.isLoading ||
      monthlyInfoQuery.isLoading ||
      voyageInfoQuery.isLoading ||
      voyageNotesQuery.isLoading ||
      bunkerInfoQuery.isLoading,
    isError:
      detailsQuery.isError ||
      poolPointsQuery.isError ||
      monthlyInfoQuery.isError ||
      voyageInfoQuery.isError ||
      voyageNotesQuery.isError ||
      bunkerInfoQuery.isError,
    error:
      detailsQuery.error ||
      poolPointsQuery.error ||
      monthlyInfoQuery.error ||
      voyageInfoQuery.error ||
      voyageNotesQuery.error ||
      bunkerInfoQuery.error,
  };
}

/**
 * Individual vessel detail hooks for granular control
 */
export function useVesselDetails(
  fileId: number,
  vesselId: number,
  enabled = true
): UseQueryResult<VesselDetails> {
  return useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'details'),
    queryFn: () => PoolReportsService.getVesselDetails(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });
}

export function useVesselPoolPoints(
  fileId: number,
  vesselId: number,
  enabled = true
): UseQueryResult<VesselPoolPoints[]> {
  return useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'poolpoints'),
    queryFn: () => PoolReportsService.getVesselPoolPoints(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });
}

export function useVesselMonthlyInfo(
  fileId: number,
  vesselId: number,
  enabled = true
): UseQueryResult<VesselMonthlyInfo[]> {
  return useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'monthlyinfo'),
    queryFn: () => PoolReportsService.getVesselMonthlyInfo(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });
}

export function useVesselVoyageInfo(
  fileId: number,
  vesselId: number,
  enabled = true
): UseQueryResult<VesselVoyageInfo[]> {
  return useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'voyageinfo'),
    queryFn: () => PoolReportsService.getVesselVoyageInfo(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });
}

export function useVesselVoyageNotes(
  fileId: number,
  vesselId: number,
  enabled = true
): UseQueryResult<VesselVoyageNote[]> {
  return useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'voyagenotes'),
    queryFn: () => PoolReportsService.getVesselVoyageNotes(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });
}

export function useVesselBunkerInfo(
  fileId: number,
  vesselId: number,
  enabled = true
): UseQueryResult<VesselBunkerInfo[]> {
  return useQuery({
    queryKey: poolReportsKeys.vesselDetails(fileId, vesselId, 'bunkerinfo'),
    queryFn: () => PoolReportsService.getVesselBunkerInfo(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });
}

// ========== NET ALLOCATION TAB HOOKS ==========

/**
 * Fetch net allocations list
 */
export function useNetAllocations(fileId: number, enabled = true): UseQueryResult<NetAllocation[]> {
  return useQuery({
    queryKey: poolReportsKeys.netAllocation(fileId),
    queryFn: () => PoolReportsService.getNetAllocations(fileId),
    enabled: enabled && !!fileId,
  });
}

/**
 * Fetch net allocation owner details and vessels
 */
export function useNetAllocationOwner(fileId: number, ownerId: number, enabled = true) {
  const vesselsQuery = useQuery({
    queryKey: [...poolReportsKeys.netAllocationOwner(fileId, ownerId), 'vessels'],
    queryFn: () => PoolReportsService.getNetAllocationOwnerVessels(fileId, ownerId),
    enabled: enabled && !!fileId && !!ownerId,
  });

  return {
    details: undefined,
    vessels: vesselsQuery.data,
    isLoading: vesselsQuery.isLoading,
    isError: vesselsQuery.isError,
    error: vesselsQuery.error,
  };
}

// ========== DISTRIBUTIONS TAB HOOKS ==========

/**
 * Fetch distribution dates
 */
export function useDistributionDates(
  fileId: number,
  enabled = true
): UseQueryResult<DistributionDate[]> {
  return useQuery({
    queryKey: poolReportsKeys.distributionDates(fileId),
    queryFn: () => PoolReportsService.getDistributionDates(fileId),
    enabled: enabled && !!fileId,
  });
}

/**
 * Fetch distributions for a specific date
 */
export function useDistributions(
  fileId: number,
  date: string,
  enabled = true
): UseQueryResult<Distribution[]> {
  return useQuery({
    queryKey: poolReportsKeys.distribution(fileId, date),
    queryFn: () => PoolReportsService.getDistributions(fileId, date),
    enabled: enabled && !!fileId && !!date,
  });
}

/**
 * Fetch distributions for a specific owner
 */
export function useDistributionsByOwner(
  fileId: number,
  ownerId: number,
  date: string,
  enabled = true
): UseQueryResult<Distribution[]> {
  return useQuery({
    queryKey: poolReportsKeys.distributionOwner(fileId, ownerId, date),
    queryFn: () => PoolReportsService.getDistributionsByOwnerId(fileId, ownerId, date),
    enabled: enabled && !!fileId && !!ownerId && !!date,
  });
}

// ========== OFFHIRE TAB HOOKS ==========

/**
 * Fetch offhires list
 */
export function useOffhires(fileId: number, enabled = true): UseQueryResult<Offhire[]> {
  return useQuery({
    queryKey: poolReportsKeys.offhire(fileId),
    queryFn: () => PoolReportsService.getOffhires(fileId),
    enabled: enabled && !!fileId,
  });
}

/**
 * Fetch offhire owner details and vessels
 */
export function useOffhireOwner(fileId: number, ownerId: number, enabled = true) {
  const detailsQuery = useQuery({
    queryKey: [...poolReportsKeys.offhireOwner(fileId, ownerId), 'details'],
    queryFn: () => PoolReportsService.getOffhireOwnerDetails(fileId, ownerId),
    enabled: enabled && !!fileId && !!ownerId,
  });

  const vesselsQuery = useQuery({
    queryKey: [...poolReportsKeys.offhireOwner(fileId, ownerId), 'vessels'],
    queryFn: () => PoolReportsService.getOffhireOwnerVessels(fileId, ownerId),
    enabled: enabled && !!fileId && !!ownerId,
  });

  return {
    details: detailsQuery.data,
    vessels: vesselsQuery.data,
    isLoading: detailsQuery.isLoading || vesselsQuery.isLoading,
    isError: detailsQuery.isError || vesselsQuery.isError,
    error: detailsQuery.error || vesselsQuery.error,
  };
}

/**
 * Fetch offhire vessel breakdown
 */
export function useOffhireVesselBreakdown(
  fileId: number,
  vesselId: number,
  enabled = true
): UseQueryResult<Offhire[]> {
  return useQuery({
    queryKey: poolReportsKeys.offhireVessel(fileId, vesselId),
    queryFn: () => PoolReportsService.getOffhireVesselBreakdown(fileId, vesselId),
    enabled: enabled && !!fileId && !!vesselId,
  });
}

// ========== DOWNLOADS TAB HOOKS ==========

/**
 * Fetch downloadable files info
 */
export function useDownloadables(fileId: number, enabled = true): UseQueryResult<Downloadable> {
  return useQuery({
    queryKey: poolReportsKeys.download(fileId),
    queryFn: () => PoolReportsService.getDownloadables(fileId),
    enabled: enabled && !!fileId,
  });
}
